import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { PatientDiagnosis, UUID } from "@/core/types";

import { getPatientDiagnosis } from "../queryFunctions/getPatientDiagnosis";
import { patientDetailKeys } from "../keyFactory";

type UsePatientDiagnosisProps<TData = PatientDiagnosis> = {
  patientId: UUID;
  select?: (data: PatientDiagnosis) => TData;
};

export function usePatientDiagnosis<TData = PatientDiagnosis>({
  patientId,
  select,
}: UsePatientDiagnosisProps<TData>) {
  return useQuery({ ...optionFactory(patientId), select });
}

type PatientDiagnosisQueryOptions = UseQueryOptions<
  PatientDiagnosis,
  unknown,
  PatientDiagnosis,
  ReturnType<typeof patientDetailKeys.diagnosis>
>;

// the exported factory lets callers do queryClient.prefetchQuery(optionFactory(patientId))
// or queryClient.getQueryData(optionFactory(id).queryKey) with correct typing
// without duplicating the key or queryFn
export function optionFactory(patientId: UUID) {
  return {
    queryFn: () => getPatientDiagnosis(patientId),
    queryKey: patientDetailKeys.diagnosis(patientId),
    staleTime: 5 * 60 * 1000,
  } satisfies PatientDiagnosisQueryOptions;
}
